import React, { MouseEvent, useState, useEffect, useRef } from 'react';
import { SidebarMenuGroup } from '../sidebar-menu-group';
import { getActiveRuleSetFromUrl } from '../../../utils/UrlUtil';
import { SidebarRoleSwitcher } from '../../SidebarRoleSwitcher';
import { SidebarSwitcher } from '../../SidebarSwitcher';
import { WindowHelperWrapper } from '../../../utils/WindowHelper';
import { cloneDeep } from 'lodash';
import { MenuData, MenuItem, SidebarMenuProps } from './types';
import { useOutsideClick, Show } from '@chakra-ui/react';
import { onExecuteKeyDown } from '../../../utils/KeyboardUtil';
import { sanitizeUrl } from '@braintree/sanitize-url';
import { ChakraUIProvider } from '../../../react-on-rails/ChakraUIProvider';
import { IconButton } from '../../../core';
import { i18n } from '../../../i18n/translate';

const EXPANDED_SIDEBAR_BREAKPOINT = 1080;

const SCORM_MANAGER_BLACKLIST = Object.freeze([
  /cportal\/teams/,
  /cportal\/user_uploads/,
  /cportal\/rosters/,
  /cportal\/categories/,
  /cportal\/assignments/,
  /cportal\/templates/,
  /cportal\/training_periods/,
  /cportal\/identity_providers/,
  /cportal\/data_integration/,
  /cportal\/webhooks/,
]);

export const SidebarMenu: React.FC<SidebarMenuProps> = ({
  currentRuleSets,
  currentUrl,
  frontEndDomain,
  menuData,
  navigate,
  windowOverride,
  toggleButtonTitle,
  buttonAriaLabel,
  options,
  theme,
}) => {
  const serverSide = typeof window === 'undefined';
  const windowHelper = WindowHelperWrapper(windowOverride);

  const isCurrentURL = (currentUrl: string) => {
    return (sub_item: MenuItem) => sub_item.target_url && sub_item.target_url.split('#')[0] === currentUrl;
  };

  const calculateShowChildren = (menuData: MenuData, currentUrl: string) => {
    const items = (menuData.submenu_items || []).map(item => {
      const showChildren = item.submenu_items && item.submenu_items.some(isCurrentURL(currentUrl));
      return { ...item, showChildren };
    });
    return { ...menuData, submenu_items: items };
  };

  const [state, setState] = useState({
    serverSide,
    menuData: calculateShowChildren(menuData, currentUrl),
    currentUrl,
    sidebarExpanded: false,
  });
  const nodeRef = useRef<HTMLElement | null>(null);

  const getForcedExpandedState = () =>
    typeof window !== 'undefined' ? window.innerHeight > EXPANDED_SIDEBAR_BREAKPOINT : false;

  useOutsideClick({
    ref: nodeRef,
    enabled: state.sidebarExpanded,
    handler: () => {
      setState(prev => ({ ...prev, sidebarExpanded: false }));
    },
  });

  useEffect(() => {
    if (!state.serverSide) {
      const activeRuleSet = getActiveRuleSetFromUrl(currentRuleSets);
      if (activeRuleSet && isScormManagerAndOnBlacklist(activeRuleSet.name)) {
        redirectToInterstitial(activeRuleSet.name);
      }
    }
  }, [state.serverSide]);

  useEffect(() => {
    if (menuData) {
      setState(prevState => ({
        ...prevState,
        menuData: calculateShowChildren(menuData, currentUrl),
      }));
    }
  }, [menuData]);

  useEffect(() => {
    if (currentUrl) {
      setState(prevState => ({ ...prevState, currentUrl }));
    }
  }, [currentUrl]);

  const pathIsWhitelisted = () => {
    const path = window.location.pathname;
    return (menuData.permitted_whitelist || []).some(permittedPath => path.includes(permittedPath));
  };

  const pathOnScormManagerBlackList = () => {
    const url = window.location.pathname + window.location.search + window.location.hash;
    return SCORM_MANAGER_BLACKLIST.some(regexp => regexp.test(url));
  };

  const isScormManagerAndOnBlacklist = (ruleSet: string) => {
    return ruleSet === 'scorm_manager' && pathOnScormManagerBlackList();
  };

  const redirectToInterstitial = (ruleSet: string) => {
    const redirect = currentRuleSets.find(currentRuleSet => currentRuleSet.name === ruleSet)?.interstitial_redirect;
    if (!redirect) return;

    windowHelper.redirect(sanitizeUrl(redirect));
  };

  const replaceCurrenRuleSetName = (ruleSet: string) => {
    const newUrl = new URL(currentUrl);
    newUrl.searchParams.set('current_rule_set_name', ruleSet);
    windowHelper.redirect(encodeURI(sanitizeUrl(newUrl.toString())));
  };

  const onSelectRuleSet = (ruleSet: string) => {
    if (pathIsWhitelisted() || isScormManagerAndOnBlacklist(ruleSet)) {
      redirectToInterstitial(ruleSet);
    } else {
      replaceCurrenRuleSetName(ruleSet);
    }
  };

  const toggleSidebar = () => {
    if (window.innerWidth < EXPANDED_SIDEBAR_BREAKPOINT) {
      setState(prevState => ({
        ...prevState,
        menuData: {
          ...prevState.menuData,
          submenu_items: prevState.menuData.submenu_items.map(item => ({ ...item, showChildren: false })),
        },
        sidebarExpanded: !prevState.sidebarExpanded,
      }));
    }
  };

  const onGroupSelect = (item: MenuItem, e: MouseEvent<HTMLAnchorElement>) => {
    if (item.submenu_items && item.submenu_items.length > 0) {
      item.showChildren = !item.showChildren;
      setState(prev => ({ ...prev, sidebarExpanded: true }));
    } else {
      if (item.target_url) {
        if (item.new_window) {
          windowHelper.open(item.target_url, '_blank');
        } else {
          setCurrentUrl(item.target_url);
          if (navigate && item.target_url.includes(frontEndDomain)) {
            e.stopPropagation();
            e.preventDefault();
            navigate(item.target_url.replace(frontEndDomain, ''));
          } else {
            windowHelper.redirect(item.target_url);
          }
        }
      }
    }
  };

  const onRoleSwitcherOpen = () => {
    if (!state.sidebarExpanded) {
      toggleSidebar();
    }
  };

  const setCurrentUrl = (url: string) => {
    const updatedMenuData = cloneDeep(state.menuData);
    updatedMenuData.submenu_items.forEach(item => {
      const itemContainsUrl = item.submenu_items && item.submenu_items.some(sub => sub.target_url === url);
      if (!itemContainsUrl) {
        item.showChildren = false;
      }
    });
    setState(prevState => ({ ...prevState, currentUrl: url, menuData: updatedMenuData, sidebarExpanded: false }));
  };
  return (
    <ChakraUIProvider theme={theme}>
      <nav
        ref={nodeRef}
        id="sidebar"
        className={'nav sidebar' + (state.sidebarExpanded ? ' show-labels' : '')}
        role="navigation"
      >
        <ul className="sidebar-menu" id="side-menu" style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
          <SidebarRoleSwitcher
            // @ts-ignore TODO: Migrate to TS
            currentRuleSets={currentRuleSets}
            onMenuOpen={onRoleSwitcherOpen}
            onSelectRuleSet={onSelectRuleSet}
          />
          <li>
            <SidebarSwitcher options={options} />
          </li>
          {state.menuData.submenu_items.map(item => (
            <li key={item.title}>
              <SidebarMenuGroup
                currentUrl={state.currentUrl}
                frontEndDomain={frontEndDomain}
                navigate={navigate}
                item={item}
                key={item.title}
                onSelect={onGroupSelect}
                setCurrentUrl={setCurrentUrl}
                sidebarExpanded={getForcedExpandedState() || state.sidebarExpanded}
              />
            </li>
          ))}
          <Show below="lg">
            <li>
              <IconButton
                ariaLabel={
                  buttonAriaLabel ||
                  toggleButtonTitle ||
                  (state.sidebarExpanded ? i18n.t('Collapse sidebar') : i18n.t('Expand sidebar'))
                }
                icon={state.sidebarExpanded ? { iconName: 'angle-left' } : { iconName: 'angle-right' }}
                onClick={toggleSidebar}
                variant="ghost"
                onKeyDown={onExecuteKeyDown(toggleSidebar)}
                w={'full'}
                borderRadius={0}
                className="toggle-control"
                data-action="toggle-control"
                title={toggleButtonTitle}
              />
            </li>
          </Show>
        </ul>
      </nav>
    </ChakraUIProvider>
  );
};

SidebarMenu.displayName = 'SidebarMenu';
